// hooks/useHealthMetrics.js
import {useMemo} from 'react';
import {calculateHealthMetrics} from '../utils/healthCalculations';

export const useHealthMetrics = (resultsData, age) => {
  return useMemo(() => {
    if (!resultsData) return null;

    const metrics = {
      heartHealth: {
        name: 'Heart Health',
        value: resultsData.heartHealth?.score || 0,
        results: [
          {
            name: 'Blood Pressure',
            value: resultsData.heartHealth?.bloodPressureSystolic || 0,
            diastolicbp: resultsData.heartHealth?.bloodPressureDiastolic || 0,
            symbol: '/others/mmh.svg',
            label: 'mmHg',
            ratings: {
              low: [0, 90],
              optimal: [90, 120],
              normal: [120, 130],
              elevated: [130, 140],
              high: [140, 180],
            },
          },
          {
            name: 'Irregular Heartbeat Count',
            value: resultsData.heartHealth?.irregularHeartBeatCount || 0,
            symbol: '/others/stethoscope.svg',
            label: 'beats',
            ratings: {
              normal: [0, 0],
              elevated: [1, 4],
              high: [5, 10],
            },
          },
          {
            name: 'Heart Rate',
            value: resultsData.heartHealth?.pulseRate || 0,
            symbol: '/others/heart-sharp.svg',
            label: 'bpm',
            ratings: {
              low: [0, 40],
              optimal: [40, 60],
              normal: [60, 100],
              elevated: [100, 140],
            },
          },
        ],
      },
      bodyFunction: {
        name: 'Body Function',
        value: resultsData.bodyFunction?.score || 0,
        results: [
          {
            name: 'Mental Stress Index',
            value: resultsData.bodyFunction?.stressIndex || 0,
            symbol: '/others/brain.svg',
            label: '',
            ratings: {
              optimal: [1, 2],
              normal: [2, 3],
              'high normal': [3, 4],
              elevated: [4, 5],
            },
          },
          {
            name: 'Cardiac Workload',
            value: resultsData.bodyFunction?.cardiacWorkload || 0,
            symbol: '/others/heart-pulse.svg',
            label: 'dB',
            ratings: {
              optimal: [0, 3.8],
              normal: [3.8, 3.9],
              'high normal': [3.9, 4.08],
              elevated: [4.08, 4.18],
              high: [4.18, 8.28],
            },
          },
          {
            name: 'Heart Rate Variability',
            value: resultsData.bodyFunction?.heartRateVariability || 0,
            symbol: '/others/stethoscope.svg',
            label: 'ms',
            ratings: {
              'very low': [0, 10.8],
              low: [10.8, 16.4],
              'low normal': [16.4, 35.5],
              normal: [35.5, 51.5],
              optimal: [51.5, 80],
            },
          },
          {
            name: 'Vascular Capacity',
            value: resultsData.bodyFunction?.vascularCapacity || 0,
            symbol: '/others/droplet.svg',
            label: 'sec',
            ratings: {
              'very low': [0, 0.79],
              low: [0.79, 1.12],
              'low normal': [1.12, 1.78],
              normal: [1.78, 2.11],
              optimal: [2.11, 15],
            },
          },
        ],
      },
      physicalHealth: {
        name: 'Physical Health',
        value: resultsData.physicalHealth?.score || 0,
        results: [
          {
            name: 'Body Mass Index',
            value: resultsData.physicalHealth?.bodyMassIndex || 0,
            symbol: '/others/weight-hanging.svg',
            label: 'kg/m²',
            ratings: {
              scheme: 'bmi',
              underweight: [0, 18.5],
              normal: [18.5, 25],
              overweight: [25, 30],
              obese: [30, 60],
            },
          },
          {
            name: 'Body Shape Index',
            value: resultsData.physicalHealth?.bodyShapeIndex || 0,
            symbol: '/others/weight-scale.svg',
            label: '',
            ratings: {
              optimal: [0, 6.8],
              normal: [7.1, 7.6],
              'high normal': [7.6, 8.6],
              elevated: [8.6, 9.1],
              high: [9.1, 9.6],
            },
          },
          {
            name: 'Breathing Rate',
            value: resultsData.heartHealth?.respiration || 0,
            symbol: '/others/wind.svg',
            label: 'brpm',
            ratings: {
              low: [0, 11],
              optimal: [12, 24],
              high: [25, 35],
            },
          },
        ],
      },
      diseaseRisk: {
        name: 'Overall Disease Risk',
        value: resultsData.diseaseRisk?.score || 0,
        results: [
          {
            name: 'Cardiovascular Risk',
            value: resultsData.diseaseRisk?.cardiovascularRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 5],
              low: [5, 7.25],
              moderate: [7.25, 10],
              high: [10, 20],
              'very high': [20, 100],
            },
          },
          {
            name: 'Hypertriglyceridemia Risk',
            value: resultsData.diseaseRisk?.hypertriglyceridemiaRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 25],
              low: [25, 45],
              moderate: [45, 55],
              high: [55, 77.5],
              'very high': [77.5, 100],
            },
          },
          {
            name: 'Heart Disease Risk',
            value: resultsData.diseaseRisk?.heartDiseaseRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 5],
              low: [5, 7.25],
              moderate: [7.25, 10],
              high: [10, 20],
              'very high': [20, 100],
            },
          },
          {
            name: 'Stroke Risk',
            value: resultsData.diseaseRisk?.strokeRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 3.3],
              low: [3.3, 4.79],
              moderate: [4.79, 6.6],
              high: [6.6, 13.2],
              'very high': [13.2, 100],
            },
          },
        ],
      },
      metabolicHealth: {
        name: 'Metabolic Health',
        value: resultsData.metabolicHealth?.diabetesRisk || 0,
        results: [
          {
            name: 'Risk of HbA1c >5.7%',
            value: resultsData.metabolicHealth?.hba1cRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 25],
              low: [25, 45],
              moderate: [45, 55],
              high: [55, 77.5],
              'very high': [77.5, 100],
            },
          },
          {
            name: 'Fasting Blood Glucose >5.5mmol/L',
            value: resultsData.metabolicHealth?.bloodGlucose || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 25],
              low: [25, 45],
              moderate: [45, 55],
              high: [55, 77.5],
              'very high': [77.5, 100],
            },
          },
          {
            name: 'Type 2 Diabetes Risk',
            value: resultsData.metabolicHealth?.diabetesRisk || 0,
            percentSign: '%',
            ratings: {
              scheme: 'risk',
              'very low': [0, 25],
              low: [25, 45],
              moderate: [45, 55],
              high: [55, 77.5],
              'very high': [77.5, 100],
            },
          },
        ],
      },
    };

    // Add skin health for default organization
    if (age && resultsData.physicalHealth?.facialSkinAge !== undefined) {
      metrics.skinHealth = {
        name: 'Skin Health',
        value: calculateHealthMetrics.skinHealthScore(resultsData, age),
        results: [
          {
            name: 'Facial Skin Age',
            value: resultsData.physicalHealth.facialSkinAge || age,
            symbol: '/others/womanface.svg',
            label: 'years',
            ratings: {
              scheme: 'age',
              younger: [0, age],
              normal: [age, age + 1],
              older: [age + 1, 100],
            },
          },
          {
            name: 'Skin Clarity',
            value: calculateHealthMetrics.skinClarityScore(),
            symbol: '/others/acne.png',
            label: '/100',
            ratings: {
              scheme: 'acne_health',
              poor: [0, 30],
              moderate: [30, 70],
              good: [70, 100],
            },
          },
          {
            name: 'Skin Tone',
            value: calculateHealthMetrics.skinToneScore(),
            symbol: '/others/skin-tone.png',
            label: '/100',
            ratings: {
              scheme: 'tone_quality',
              poor: [0, 30],
              fair: [30, 50],
              good: [50, 70],
              very_good: [70, 85],
              excellent: [85, 100],
            },
          },
          {
            name: 'Skin Balance',
            value: calculateHealthMetrics.skinBalanceScore(),
            symbol: '/others/droplet.svg',
            label: '/100',
            ratings: {
              scheme: 'skin_balance',
              needs_care: [0, 70],
              balanced: [70, 95],
              optimal: [95, 100],
            },
          },
        ],
      };
    }

    return metrics;
  }, [resultsData, age]);
};
